import { createSelector } from "@reduxjs/toolkit";
import {
  openapi,
  PostFunctionsDrofusRoomsOccurrencesLiveApiArg,
  PostFunctionsDrofusRoomsOccurrencesLiveApiResponse,
} from "../../openapi";
import { IRootState } from "../app/store";
import { selectProjectNo } from "../globalSelectors";
import { selectHttpSingleRoomFull } from "../Room/httpRoomSelectors";

export type IDrofusLiveOccurrenceItem =
  PostFunctionsDrofusRoomsOccurrencesLiveApiResponse["items"][number];

export type IDrofusLiveOccurrenceRow = IDrofusLiveOccurrenceItem & {
  id: string;
  displayName: string;
  modelQuantityForCompCode: number;
  modelQuantityDelta: number | null;
};

export const selectDrofusOccurrenceValidationSource = (state: IRootState) =>
  state.drofusOccurrenceValidation.specifiedArticlesSource;

export const selectDrofusRoomOccurrencesLiveActiveListType = (state: IRootState) =>
  state.drofusOccurrenceValidation.liveOccurrencesListType;

export const selectDrofusRoomOccurrencesLiveApiArg = createSelector(
  [selectProjectNo, selectHttpSingleRoomFull],
  (projectNo, room): PostFunctionsDrofusRoomsOccurrencesLiveApiArg | null => {
    if (!projectNo || !room?.roomNumber) return null;

    return {
      body: {
        projectNo,
        roomNumber: room.roomNumber,
        maxRows: 1000,
        jRoomsOnly: true,
      },
    };
  }
);

export const selectDrofusRoomOccurrencesLiveQueryState = createSelector(
  [selectDrofusRoomOccurrencesLiveApiArg, (state: IRootState) => state],
  (arg, state) => (arg ? openapi.endpoints.postFunctionsDrofusRoomsOccurrencesLive.select(arg)(state) : null)
);

export const selectDrofusRoomOccurrencesLiveData = createSelector(
  [selectDrofusRoomOccurrencesLiveQueryState],
  (queryState) => queryState?.data ?? null
);

export const selectModelQuantityByCompCodeInSelectedRoomSnapshot = createSelector(
  [selectHttpSingleRoomFull],
  (room): Record<string, number> => {
    const out: Record<string, number> = {};
    const snapshotItems = room?.mcbimItemsSnapshot?.items ?? [];

    for (const item of snapshotItems) {
      const compCode = item.compCode?.trim();
      if (!compCode) continue;
      out[compCode] = (out[compCode] ?? 0) + 1;
    }

    return out;
  }
);

export const selectDrofusRoomOccurrencesLiveRows = createSelector(
  [selectDrofusRoomOccurrencesLiveData, selectModelQuantityByCompCodeInSelectedRoomSnapshot],
  (liveData, modelQuantityByCompCode): IDrofusLiveOccurrenceRow[] => {
    return (liveData?.items ?? [])
      .map((item, index) => {
        const compCode = item.compCode?.trim() || null;
        const modelQuantityForCompCode = compCode ? modelQuantityByCompCode[compCode] ?? 0 : 0;
        const modelQuantityDelta =
          item.quantity == null || item.articleToBeDrawn === false || item.sourceValues.parent_occurrence_id != null
            ? null : modelQuantityForCompCode - item.quantity;

        return {
          ...item,
          id: String(item.occurrenceId ?? `${compCode ?? "row"}-${index}`),
          displayName: item.articleName ?? item.nameOrItemName ?? "",
          modelQuantityForCompCode,
          modelQuantityDelta,
        };
      })
      .sort((a, b) => {
        const codeCompare = String(a.compCode ?? "").localeCompare(String(b.compCode ?? ""));
        if (codeCompare !== 0) return codeCompare;
        return String(a.displayName).localeCompare(String(b.displayName));
      });
  }
);

export const selectDrofusRoomOccurrencesLiveSummary = createSelector(
  [selectDrofusRoomOccurrencesLiveData],
  (liveData) => {
    const items = liveData?.items ?? [];
    const listTypes = new Set<string>();
    const responsibilities = new Set<string>();

    for (const item of items) {
      if (item.sourceValues.parent_occurrence_id == null && item.equipmentListTypeName) listTypes.add(item.equipmentListTypeName);
      if (item.articleResponsibility) responsibilities.add(item.articleResponsibility);
    }

    return {
      rowCount: liveData?.fetchedRows ?? 0,
      fetchedAt: liveData?.fetchedAt ?? null,
      elapsedMs: liveData?.elapsedMs ?? null,
      listTypes: Array.from(listTypes).sort((a, b) => a.localeCompare(b)),
      responsibilities: Array.from(responsibilities).sort((a, b) => a.localeCompare(b)),
    };
  }
);

export const selectDrofusRoomOccurrencesLiveListTypeItems = createSelector(
  [selectDrofusRoomOccurrencesLiveSummary],
  (summary) => [
    { value: "", label: "Alle" },
    ...summary.listTypes.map((listType) => ({ value: listType, label: listType })),
  ]
);

export const selectDrofusRoomOccurrencesLiveVisibleRows = createSelector(
  [selectDrofusRoomOccurrencesLiveRows, selectDrofusRoomOccurrencesLiveActiveListType],
  (rows, activeListType): IDrofusLiveOccurrenceRow[] => {
    if (!activeListType) return rows;
    return rows.filter((row) => row.equipmentListTypeName === activeListType);
  }
);

export const selectDrofusRoomOccurrencesLiveResolvedListType = createSelector(
  [selectDrofusRoomOccurrencesLiveSummary, selectDrofusRoomOccurrencesLiveActiveListType],
  (summary, activeListType): string | null => {
    if (!activeListType) return null;
    return summary.listTypes.includes(activeListType) ? activeListType : null;
  }
);
